import React, { useEffect, useState } from 'react';
import LcLoading from '../../../../components/Generic/LcLoading';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import { PatchManagerService } from '../../../../services/patchmanager/patchManagerService';
import { RootState } from '../../../../store';
import { UserState } from '../../../../store/reducers/userReducer';
import { GraphModel } from '../../../../services/patchmanager/patchManagerModel';
import LcNoData from '../../../../components/Generic/LcNoData';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

interface Props {
    selected: number;
}

const SystemsByPlatformGraph: React.FC<Props> = (props) => {
    const user = useSelector<RootState, UserState>(state => state.user);
    const [loading, setLoading] = useState<boolean>(false);
    const patchManagerService = new PatchManagerService(props);
    const [systemGraph, setSystemGraph] = useState<GraphModel[]>([]);

    const [total, setTotal] = useState(0);
    const getColorsGraph = (SOName: string): string => {
        if (SOName.includes('Windows') && SOName.includes('Server')) {
            return '#0580ce';
        }
        if (SOName.includes('Windows')) {
            return "#4ebaff";
        }
        if (SOName.includes('Linux')) {
            return '#ffa400';
        }
        if (SOName.includes('Mac')) {
            return '#777777';
        }
        return '#777777';
    }

    const getSystemPlatformDataSuported = async () => {
        try {
            const systemPlatformResponse = await patchManagerService.GetSystemByPlatformSupported();
            setSystemGraph(systemPlatformResponse);
            if (!systemPlatformResponse) {
                return;
            }
            if (systemPlatformResponse.length == 0) {
                return;
            }
            setTotal(systemPlatformResponse.reduce((sum, { value }) => Number(sum) + Number(value), 0))

        } catch (error) {
            console.error('Error:', error);
        }
    }

    const getSystemPlatformDataObsolete = async () => {
        const SOSystems = ['windows', 'windows-server', 'linux', 'macos'];
        try {
            const obsoleteResponse = await patchManagerService.GetSystemByPlatformObsolete(SOSystems);
            setSystemGraph(translateArray(obsoleteResponse));
            if (!obsoleteResponse) {
                return;
            }
            if (obsoleteResponse.length == 0) {
                return;
            }
            setTotal(obsoleteResponse.reduce((sum, { value }) => Number(sum) + Number(value), 0))

        } catch (error) {
            console.error('Error:', error);
        }
    }

    function translateArray(originalArray) {
        const nameMapping = {
            'windows': 'Windows Client',
            'windows-server': 'Windows Server',
            'linux': 'Linux',
            'macos': 'Mac'
        };

        const translatedArray = originalArray.map(item => {
            const translatedName = nameMapping[item.name] || item.name; // Use a tradução ou o valor original se não houver tradução
            return { name: translatedName, value: Number(item.value) };
        });

        return translatedArray;
    }

    function hasReduceFunction(obj: any): obj is { reduce: Function } {
        return typeof obj?.reduce === 'function';
    }

    function hasMapFunction(obj: any): obj is { reduce: Function } {
        return typeof obj?.map === 'function';
    }

    useEffect(() => {
        console.log(props.selected)
        setLoading(true);
        props.selected === 1 ?
            getSystemPlatformDataSuported()
                .then(() => setLoading(false))
            :
            getSystemPlatformDataObsolete()
                .then(() => setLoading(false))
    }, [user.ClientGroupSelected, user.refreshFlag, props.selected]);

    function getDataOptions(systemGraphData: GraphModel[]) {
        const dataLookup = systemGraphData && systemGraphData.length > 0
            && hasReduceFunction(systemGraphData) ?
            systemGraphData.reduce((acc, item) => {
            acc[item.name] = Number(item.value);
            return acc;
        }, {}) : [];

        const options = {
            tooltip: {
                backgroundColor: '#f1f5fb',
                borderWidth: 0,
                textStyle: {
                    fontSize: 11,
                    fontFamily: 'Inter',
                    color: '#2B304F',
                },
                trigger: 'item'
            },
            legend: {
                type: 'scroll', // Ensure paging by using a scroll type legend
                orient: 'horizontal',
                right: '0%',
                bottom: '10%',
                top: 'bottom',
                textStyle: {
                    fontSize: 9,
                    fontFamily: 'Inter',
                    color: '#000', // Ensure text color is black, which might influence icon color
                },
                pageTextStyle: {
                    color: '#000', // Directly set text and icon color (hypothetical)
                },
                pageButtonItemGap: 5,
                pageButtonGap: 10,
                pageButtonColor: '#000', // Background color of the page button, set to black
                pageButtonPosition: 'end',
                pageFormatter: '{current}/{total}',
                pageIcons: {
                    horizontal: [
                        'path://M0,0 L12,-12', // up arrow
                        'path://M0,12 L12,0' // down arrow
                    ]
                },
                formatter: function (name) {
                    const value = dataLookup[name] || 0;
                    return `${name}: ${Number(value).toLocaleString('pt-BR')}`;
                },
            },
            series: [{
                type: 'pie',
                center: ['50%', '50%'],
                radius: ['40%', '70%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 4,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                label: {
                    show: true,
                    position: 'center',
                    formatter: function () {
                        return total; // Assuming `total` is defined in your scope
                    },
                    fontSize: 20,
                    fontWeight: 'bold',
                    textStyle: {
                        overflow: 'truncate',
                        ellipsis: '...',
                        fontFamily: 'Inter',
                    }
                },
                color: systemGraphData && hasMapFunction(systemGraphData) ? systemGraphData.map(i => getColorsGraph(i.name)) : [], // Assuming `getColorsGraph` is a function that returns colors
                data: systemGraphData,
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(58, 79, 135, 0.4)',
                        borderWidth: 0
                    }
                }
            }]
        };
        return options;
    }

    return (
        <LcLoading loading={loading} label='Carregando gráfico...'>
            {
                systemGraph && systemGraph.length > 0 ?
                    <>
                        <ReactECharts
                            opts={{ renderer: 'svg', locale: 'PT-br' }}
                            className='MissingPatchesGraph'
                            option={getDataOptions(systemGraph)}
                        />
                        {
                            props.selected === 2 &&
                            <div className='row lc-title' style={{ fontSize: '10px', alignItems: 'center', justifyContent: 'center', margin: '1rem 0', }}>
                                <AiOutlineExclamationCircle color='#3498d7' fontSize={15} />
                                <strong> Sistemas Obsoletos:</strong> não recebem mais atuallizações de segurança do fabricante do SO.
                            </div>
                        }
                    </>
                    :
                    <LcNoData size="default" /> 
            }
        </LcLoading>
    );
}

export default SystemsByPlatformGraph;